/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/**
 * Quick Link Metrics Dialog Component
 * Displays usage statistics and logs for a quick link
 */

import { CommonModule } from "@angular/common";
import { Component, OnInit, OnDestroy, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

// PrimeNG imports
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { ChipModule } from "primeng/chip";
import { DatePickerModule } from "primeng/datepicker";
import { DividerModule } from "primeng/divider";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { PaginatorModule } from "primeng/paginator";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";

// Our imports
import { QPilotAccessToken } from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import { QuickLinkMetrics } from "../../interfaces/quick-link-api.interface";
import { QuickLinksService } from "../../services/quick-links.service";

export interface QuickLinkMetricsDialogData {
  quickLinkId: number;
  quickLinkName: string;
  siteId: number;
  embeddedMode?: boolean;
  accessToken?: QPilotAccessToken;
}

@Component({
  selector: "app-quick-link-metrics-dialog",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    ChipModule,
    DatePickerModule,
    DividerModule,
    PaginatorModule,
    ProgressSpinnerModule,
    SkeletonModule,
    TableModule,
    TagModule,
  ],
  templateUrl: "./quick-link-metrics-dialog.component.html",
  styleUrl: "./quick-link-metrics-dialog.component.scss",
})
export class QuickLinkMetricsDialogComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Data from parent
  quickLinkId!: number;
  quickLinkName!: string;
  siteId!: number;
  embeddedMode: boolean = false;
  private accessToken?: QPilotAccessToken;

  // State
  isLoading = signal<boolean>(false);
  metrics = signal<QuickLinkMetrics | null>(null);

  // Pagination
  currentPage = 1;
  pageSize = 25;

  // Date range filter (optional) - using PrimeNG DatePicker range mode with FormControl
  dateRangeControl = new FormControl<Date[] | null>(null);

  constructor(
    private quickLinksService: QuickLinksService,
    private sessionService: SessionService,
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
  ) {}

  ngOnInit(): void {
    // Extract data from dialog config
    const data = this.config.data as QuickLinkMetricsDialogData;
    this.quickLinkId = data.quickLinkId;
    this.quickLinkName = data.quickLinkName;
    this.siteId = data.siteId;
    this.embeddedMode = data.embeddedMode ?? false;
    this.accessToken = data.accessToken;

    // Load initial metrics
    this.loadMetrics();
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    // In embedded mode, use accessToken from dialog data
    if (this.embeddedMode && this.accessToken) {
      return this.accessToken;
    }
    // In standard mode, use session service (convert null to undefined)
    return this.sessionService.accessToken ?? undefined;
  }

  /**
   * Load metrics from API
   */
  loadMetrics(): void {
    this.isLoading.set(true);

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      console.error("No access token available");
      this.isLoading.set(false);
      return;
    }

    // Extract start and end dates from range
    const dateRange = this.dateRangeControl.value;
    const startDate = dateRange?.[0] ?? undefined;
    const endDate = dateRange?.[1] ?? undefined;

    this.quickLinksService
      .getQuickLinkMetrics(
        this.siteId,
        this.quickLinkId,
        accessToken,
        this.currentPage,
        this.pageSize,
        startDate,
        endDate,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.metrics.set(response);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error("Failed to load metrics:", error);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Handle page change
   */
  onPageChange(event: any): void {
    this.currentPage = event.page + 1; // PrimeNG uses 0-based page index
    this.pageSize = event.rows;
    this.loadMetrics();
  }

  /**
   * Handle date range selection - auto-triggers on complete range selection
   */
  onDateRangeSelect(): void {
    const dateRange = this.dateRangeControl.value;
    // Only trigger if both dates are selected (complete range)
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      this.currentPage = 1; // Reset to first page
      this.loadMetrics();
    }
  }

  /**
   * Handle date range clear
   */
  onDateRangeClear(): void {
    this.currentPage = 1;
    this.loadMetrics();
  }

  /**
   * Get severity for success/failure tag
   */
  getSuccessSeverity(success: boolean): "success" | "danger" {
    return success ? "success" : "danger";
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  }

  /**
   * Calculate success rate percentage
   */
  getSuccessRateDisplay(): string {
    const summary = this.metrics()?.summary;
    if (!summary) return "N/A";
    // API already returns percentage (0-100), no need to multiply by 100
    return `${summary.successRate.toFixed(1)}%`;
  }

  /**
   * Export usage logs to CSV
   */
  exportToCsv(): void {
    const metricsData = this.metrics();
    if (!metricsData || !metricsData.usageLogs?.length) {
      console.warn("No data to export");
      return;
    }

    // Build CSV sections
    const csvSections: string[] = [];

    // ========================================
    // SECTION 1: Summary Metrics
    // ========================================
    csvSections.push("QUICK LINK METRICS SUMMARY");
    csvSections.push("");
    csvSections.push(`Quick Link ID,${metricsData.quickLinkId}`);
    csvSections.push(`Quick Link Name,${metricsData.quickLinkName}`);
    csvSections.push("");
    csvSections.push(`Total Uses,${metricsData.summary.totalUses}`);
    csvSections.push(`Unique Customers,${metricsData.summary.uniqueCustomers}`);
    csvSections.push(`Successful,${metricsData.summary.successCount}`);
    csvSections.push(`Failed,${metricsData.summary.failureCount}`);
    csvSections.push(
      `Success Rate,${metricsData.summary.successRate.toFixed(1)}%`,
    );
    csvSections.push(
      `First Used,${this.formatDate(metricsData.summary.firstUsedUtc)}`,
    );
    csvSections.push(
      `Last Used,${this.formatDate(metricsData.summary.lastUsedUtc)}`,
    );
    csvSections.push("");
    csvSections.push("");

    // ========================================
    // SECTION 2: Usage History Table
    // ========================================
    csvSections.push("USAGE HISTORY");
    csvSections.push("");

    // Table headers
    const headers = [
      "Date & Time",
      "Customer ID",
      "Scheduled Order ID",
      "Usage ID",
      "Status",
      "Error Message",
      "Error Code",
      "IP Address",
    ];
    csvSections.push(headers.join(","));

    // Table rows
    const rows = metricsData.usageLogs.map((log) => [
      this.formatDate(log.usedUtc),
      log.customerId,
      log.scheduledOrderId,
      log.usageId,
      log.success ? "Success" : "Failed",
      log.errorMessage || "",
      log.errorCode || "",
      log.ipAddress || "",
    ]);

    rows.forEach((row) => {
      csvSections.push(row.map((cell) => `"${cell}"`).join(","));
    });

    // Build final CSV content
    const csvContent = csvSections.join("\n");

    // Download CSV
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `quick-link-${this.quickLinkId}-metrics-${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Close dialog
   */
  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
